import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, Link } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Home,
  Users,
  Building2,
  Building,
  Briefcase,
  Target,
  Calendar,
  Activity,
  Mail,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  User,
  ChevronDown,
  Handshake,
  CheckSquare,
  Plus,
  MailPlus,
  Lightbulb,
  Sparkles,
  Trash2,
  CalendarDays,
  CalendarRange,
  Award,
  HelpCircle,
  ListFilter,
  Clock3,
  Wallet,
  MapPin,
  SearchCheck,
  Newspaper,
  NotebookPen,
  RefreshCw,
  SlidersHorizontal,
  Plane,
  ArrowLeft,
} from 'lucide-react';
import NotificationIndicator from '../notifications/NotificationIndicator';
import CalendarInviteIssueIndicator from '@/components/calendar/CalendarInviteIssueIndicator';
import { QuickNoteDialog } from '@/components/notes/QuickNoteDialog';
import { openQuickNoteComposer } from '@/lib/noteEntityContext';
import EmailModal from '@/components/modals/EmailModal';
import { FjordNavigation } from './FjordNavigation';
import { isFjordUi, uiRecoveryHref } from '@/lib/uiExperience';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import DebugConsole from '@/components/debug/DebugConsole';
import DebugButton from '@/components/debug/DebugButton';
import ContactModal from '@/components/modals/ContactModal';
import LeadModal from '@/components/modals/LeadModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import MobileBottomNav from './MobileBottomNav';
import GlobalCrmSearch from '@/components/search/GlobalCrmSearch';
import GooglePeopleSyncButton from '@/components/google-people/GooglePeopleSyncButton';
import { CalvoUrgencyCluster } from '@/components/common/CalvoUrgencyCluster';
import { useOperationalIndicators } from '@/hooks/use-active-theme';
import { MartinaPushBanner } from '@/components/exai/MartinaPushBanner';
import { MartinaUsageSuspendedBanner } from '@/components/exai/MartinaUsageSuspendedBanner';
import { useMartinaUsageControl } from '@/hooks/useMartinaUsageControl';
import RecordDrawer from '@/components/ui/RecordDrawer';
import ContactDrawerContent from '@/components/drawers/ContactDrawerContent';
import CompanyDrawerContent from '@/components/drawers/CompanyDrawerContent';
import BranchDrawerContent from '@/components/drawers/BranchDrawerContent';
import LeadDrawerContent from '@/components/drawers/LeadDrawerContent';
import DealDrawerContent from '@/components/drawers/DealDrawerContent';
import type { RecordDrawerEntity } from '@/lib/recordDrawer';
import { inferContextFromLocation, useMartinaGlobal } from '@/contexts/ExAiGlobalContext';
import {
  CRM_MUTATION_COMMITTED_EVENT,
  isCrmRealtimeResource,
  type CrmRealtimeResource,
  invalidateCrmResourceQueries,
  shouldRetryCrmQuery,
  apiRequest,
} from '@/lib/queryClient';
import { useOwnerRealtimeInvalidation } from '@/hooks/useRealTimeEmails';
import { removeCurrentCrmQueryCache } from '@/lib/persistedCrmQueryCache';
import { crmBackTarget, installCrmBackTracking } from '@/lib/crmBackNavigation';

interface MenuItemProps {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  counters?: { managed: number; urgent: number };
}

// Raggruppamenti menu per tema V2
const V2_MENU_GROUPS = [
  {
    label: 'Core',
    paths: ['/', '/feed', '/dashboard', '/contacts', '/companies', '/branches', '/brands', '/deals', '/opportunities', '/leads'],
  },
  {
    label: 'Relazioni',
    paths: ['/synergies', '/associations', '/data-explorer', '/import-export-module'],
  },
  {
    label: 'Comunicazione',
    paths: ['/emails', '/tasks', '/meetings', '/activities', '/calendar', '/travel', '/notes'],
  },
  {
    label: 'Sistema',
    paths: ['/deduplication', '/settings', '/admin/maintenance', '/help'],
  },
];

// I layout a sezioni (V2/Calvo) mostrano solo le voci elencate in
// V2_MENU_GROUPS: una voce nuova non censita sparirebbe dalla sidebar senza
// errori (è successo con /brands). Questa funzione garantisce che ogni voce
// finisca comunque in una sezione: le orfane vanno in coda sotto "Altro".
function buildMenuSections(allItems: MenuItemProps[]) {
  const sections = V2_MENU_GROUPS
    .map(group => ({
      label: group.label,
      items: allItems.filter(item => group.paths.includes(item.path)),
    }))
    .filter(section => section.items.length > 0);

  const groupedPaths = new Set(V2_MENU_GROUPS.flatMap(group => group.paths));
  const orphans = allItems.filter(item => !groupedPaths.has(item.path));
  if (orphans.length > 0) {
    sections.push({ label: 'Altro', items: orphans });
  }
  return sections;
}

const getMainMenuItems = (stats?: any): MenuItemProps[] => [
  { label: 'Feed', path: '/', icon: <Newspaper className="h-5 w-5" /> },
  { label: 'Dashboard', path: '/dashboard', icon: <Home className="h-5 w-5" /> },
  { label: 'Contatti', path: '/contacts', icon: <Users className="h-5 w-5" />, counters: stats?.counters?.contacts },
  { label: 'Aziende', path: '/companies', icon: <Building2 className="h-5 w-5" />, counters: stats?.counters?.companies },
  { label: 'Filiali', path: '/branches', icon: <Building className="h-5 w-5" />, counters: stats?.counters?.branches },
  { label: 'Brands', path: '/brands', icon: <Award className="h-5 w-5" /> },
  { label: 'Opportunità', path: '/deals', icon: <Briefcase className="h-5 w-5" />, counters: stats?.counters?.deals },
  { label: 'Opportunità AI', path: '/opportunities', icon: <Sparkles className="h-5 w-5" />, badge: stats?.opportunities },
  { label: 'Lead', path: '/leads', icon: <Target className="h-5 w-5" />, counters: stats?.counters?.leads },
  { label: 'Ricerca e modifica', path: '/ricerca', icon: <SearchCheck className="h-5 w-5" /> },
  { label: 'Sinergie', path: '/synergies', icon: <Handshake className="h-5 w-5" />, counters: stats?.counters?.synergies },
  { label: 'Associazioni', path: '/associations', icon: <Activity className="h-5 w-5" /> },
  { label: 'Esplora dati', path: '/data-explorer', icon: <ListFilter className="h-5 w-5" /> },
  { label: 'Import Sicuro', path: '/import-export-module', icon: <Lightbulb className="h-5 w-5" /> },
  { label: 'Task', path: '/tasks', icon: <CheckSquare className="h-5 w-5" />, counters: stats?.counters?.tasks },
  { label: 'Meeting', path: '/meetings', icon: <Calendar className="h-5 w-5" />, counters: stats?.counters?.meetings },
  { label: 'Attività', path: '/activities', icon: <CheckSquare className="h-5 w-5" /> },
  { label: 'Calendario', path: '/calendar', icon: <CalendarDays className="h-5 w-5" /> },
  { label: 'Taccuino Martina', path: '/notes', icon: <NotebookPen className="h-5 w-5" /> },
  { label: 'Viaggi', path: '/travel', icon: <Plane className="h-5 w-5" /> },
  { label: 'Email', path: '/emails', icon: <Mail className="h-5 w-5" />, counters: stats?.counters?.emails },
];

const systemMenuItems: MenuItemProps[] = [
  { label: 'Deduplicazione', path: '/deduplication', icon: <Trash2 className="h-5 w-5" /> },
  { label: 'Impostazioni', path: '/settings', icon: <Settings className="h-5 w-5" /> },
  { label: 'Aiuto', path: '/help', icon: <HelpCircle className="h-5 w-5" /> },
  { label: 'Manutenzione', path: '/admin/maintenance', icon: <Trash2 className="h-5 w-5" /> },
];

// Hook per rilevare tema V2 attivo
function useIsV2() {
  const [isV2, setIsV2] = useState(
    () => document.documentElement.getAttribute('data-theme') === 'v2'
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsV2(document.documentElement.getAttribute('data-theme') === 'v2');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return isV2;
}

function MenuItem({ item, isOpen, isV2 }: { item: MenuItemProps; isOpen: boolean; isV2: boolean }) {
  const [location] = useLocation();
  const isActive = location === item.path || (item.path === '/' && location === '/dashboard');

  return (
    <Link href={item.path}>
      <div className={cn(
        "flex items-center transition-colors rounded-none cursor-pointer",
        isOpen ? "gap-3 px-3 py-2" : "justify-center px-2 py-2",
        isActive
          ? isV2 ? "bg-[rgba(67,97,238,0.15)] border-l-2 border-[#4361ee]" : "bg-accent text-accent-foreground"
          : isV2 ? "hover:bg-[rgba(255,255,255,0.05)]" : "hover:bg-accent"
      )}>
        <div className={cn(
          'h-4 w-4 flex-shrink-0',
          isActive ? (isV2 ? 'opacity-100' : 'text-foreground') : (isV2 ? 'opacity-60' : 'text-muted-foreground')
        )}>
          {item.icon}
        </div>
        {isOpen && (
          <div className="flex-1 flex items-center justify-between min-w-0">
            <span className={cn(
              "text-sm truncate",
              isActive ? (isV2 ? "font-semibold" : "font-medium text-foreground") : "font-normal"
            )}>
              {item.label}
            </span>
            <div className="flex items-center gap-1 ml-1">
              {item.counters && (
                isV2 ? (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-white/10 text-white/60 tabular-nums">
                    {item.counters.managed.toLocaleString('it-IT')}
                  </span>
                ) : (
                  <>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-800 text-white rounded-sm">
                      {item.counters.managed}
                    </span>
                    {item.counters.urgent > 0 && (
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-red-600 text-white rounded-sm">
                        {item.counters.urgent}
                      </span>
                    )}
                  </>
                )
              )}
              {item.badge != null && !item.counters && (
                <span className={cn(
                  'px-2 py-0.5 text-xs font-medium flex-shrink-0',
                  isV2
                    ? 'rounded-full bg-white/10 text-white/60'
                    : item.path === '/emails'
                      ? 'rounded-none bg-destructive text-destructive-foreground'
                      : 'rounded-none bg-muted text-muted-foreground'
                )}>
                  {item.badge}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function safeOperationalTimezone(timezone?: string | null): string {
  if (!timezone) return 'Europe/Rome';
  try {
    new Intl.DateTimeFormat('it-IT', { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return 'Europe/Rome';
  }
}

function OperationalSidebarClock({ timezone, isOpen, isV2 }: { timezone?: string | null; isOpen: boolean; isV2: boolean }) {
  const [now, setNow] = useState(() => new Date());
  const safeTimezone = safeOperationalTimezone(timezone);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const fullLabel = new Intl.DateTimeFormat('it-IT', {
    timeZone: safeTimezone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);
  const compactLabel = new Intl.DateTimeFormat('it-IT', {
    timeZone: safeTimezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);
  const label = fullLabel.charAt(0).toUpperCase() + fullLabel.slice(1).replace(' alle ', ' · ');

  return (
    <div className={cn(
      'border-b border-border px-3 py-3',
      isV2 ? 'text-white/80' : 'text-foreground',
    )}>
      {isOpen ? (
        <div className="flex items-start gap-2">
          <Clock3 className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold tabular-nums">{label}</p>
            <p className="truncate text-[11px] text-muted-foreground">{safeTimezone}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 text-[10px] tabular-nums">
          <Clock3 className="h-4 w-4 text-muted-foreground" />
          <span>{compactLabel}</span>
        </div>
      )}
    </div>
  );
}

type ApiConnectionState = 'checking' | 'online' | 'offline';

interface CurrentUser {
  id: number;
  username: string;
  email: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  status: string;
  timezone?: string | null;
}

interface MartinaBriefingPreferences {
  homeSections?: {
    promemoriaAttivi?: boolean;
    pinnedModules?: string[];
  };
}

function ApiConnectionIndicator({ isOpen, isV2 }: { isOpen: boolean; isV2: boolean }) {
  const [state, setState] = useState<ApiConnectionState>('checking');

  useEffect(() => {
    let disposed = false;
    // Al mount la pagina spara decine di richieste in parallelo (asset JS
    // frammentati + tutte le query React Query iniziali): un solo timeout
    // superato in quella ressa non e' un'API davvero offline, e' contesa di
    // connessioni del browser (bug reale 21/08, segnalato da Michele: rosso
    // ad ogni ricarica). Serve una conferma — due fallimenti CONSECUTIVI —
    // prima di dichiarare offline, mai un singolo intoppo transitorio.
    let consecutiveFailures = 0;

    const checkConnection = async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch('/api/health', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (disposed) return;
        if (response.ok) {
          consecutiveFailures = 0;
          setState('online');
        } else {
          consecutiveFailures += 1;
          if (consecutiveFailures >= 2) setState('offline');
        }
      } catch {
        if (disposed) return;
        consecutiveFailures += 1;
        if (consecutiveFailures >= 2) setState('offline');
      } finally {
        window.clearTimeout(timeout);
      }
    };

    // Primo controllo posticipato: la ressa iniziale di richieste si e' gia'
    // diradata dopo ~1.5s, cosi' il primo giro non nasce gia' in coda.
    const firstCheck = window.setTimeout(() => void checkConnection(), 1500);
    const interval = window.setInterval(() => void checkConnection(), 30000);
    const handleOnline = () => void checkConnection();
    const handleOffline = () => setState('offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      disposed = true;
      window.clearTimeout(firstCheck);
      window.clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const statusText = state === 'online'
    ? 'API connesse'
    : state === 'offline'
      ? 'Connessione API assente'
      : 'Controllo connessione API';
  const statusClass = `api-connection-indicator__light api-connection-indicator__light--${state}`;

  return (
    <div
      className={cn(
        'flex items-center border-b border-border px-3 py-2 text-[11px]',
        isV2 ? 'text-white/80' : 'text-muted-foreground',
        isOpen ? 'gap-2' : 'justify-center',
      )}
      role="status"
      aria-live="polite"
      title={statusText}
    >
      <span className={statusClass} aria-hidden="true" />
      {isOpen && <span className="truncate">{statusText}</span>}
    </div>
  );
}

/**
 * Residuo reale sul provider LLM attivo (Kimi Balance Query API, 20/08):
 * mai una stima nostra, solo il numero che il vendor dichiara. Se non
 * ancora letto o il vendor non lo documenta il widget non si mostra —
 * inventare un numero sarebbe peggio di ometterlo.
 */
function ApiBudgetIndicator({ isOpen, isV2 }: { isOpen: boolean; isV2: boolean }) {
  const [, navigate] = useLocation();
  const { data } = useMartinaUsageControl();

  const balance = data?.llmRealBalance;
  if (balance?.status !== 'ok' || balance.availableUsd === null) return null;

  const label = `$${balance.availableUsd.toFixed(2)}`;
  const isLow = balance.availableUsd < 2;

  return (
    <button
      type="button"
      onClick={() => navigate('/settings/ai')}
      className={cn(
        'flex w-full items-center border-b border-border px-3 py-2 text-[11px] transition-colors',
        isV2 ? 'text-white/80 hover:text-white' : 'text-muted-foreground hover:text-foreground',
        isOpen ? 'gap-2' : 'justify-center',
      )}
      title={`Residuo reale sul provider LLM attivo: ${label}`}
    >
      <Wallet className={cn('h-3.5 w-3.5 shrink-0', isLow && 'text-[color:var(--ma-red)]')} aria-hidden="true" />
      {isOpen && (
        <span className={cn('truncate', isLow && 'text-[color:var(--ma-red)]')}>
          Residuo API: {label}
        </span>
      )}
    </button>
  );
}

/**
 * Stato Google Maps (Places/Geocoding): gruppo di controllo distinto da
 * Workspace (Calendar/Gmail/Drive). Verde = usable; rosso = kill-switch o
 * limite Maps attivo, con motivo sotto.
 */
function MapsApiStatusIndicator({ isOpen, isV2 }: { isOpen: boolean; isV2: boolean }) {
  const [, navigate] = useLocation();
  const { data } = useMartinaUsageControl();
  const maps = data?.control?.google;
  if (!maps) return null;

  const blocked = maps.suspended === true;
  const reason = maps.reason?.trim() || (blocked ? 'Google Maps sospeso dal controllo consumi.' : null);

  return (
    <button
      type="button"
      onClick={() => navigate('/settings/ai')}
      className={cn(
        'flex w-full flex-col border-b border-border px-3 py-2 text-[11px] transition-colors text-left',
        isV2 ? 'text-white/80 hover:text-white' : 'text-muted-foreground hover:text-foreground',
      )}
      title={blocked ? `Google Maps bloccato: ${reason}` : 'Google Maps operativo'}
    >
      <span className={cn('flex w-full items-center', isOpen ? 'gap-2' : 'justify-center')}>
        <MapPin
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            blocked ? 'text-[color:var(--ma-red)]' : 'text-[color:var(--ma-green)]',
          )}
          aria-hidden="true"
        />
        {isOpen && (
          <span className={cn('truncate font-medium', blocked && 'text-[color:var(--ma-red)]')}>
            Maps: {blocked ? 'bloccate' : 'operative'}
          </span>
        )}
      </span>
      {isOpen && blocked && reason ? (
        <span className="mt-1 line-clamp-3 pl-5 text-[10px] leading-snug text-[color:var(--ma-red)]">
          {reason}
        </span>
      ) : null}
    </button>
  );
}

// Sidebar con sezioni V2 / Calvo o flat (default)
function SidebarNav({ items, systemItems, isOpen, isV2, isCalvo, stats }: {
  items: MenuItemProps[];
  systemItems: MenuItemProps[];
  isOpen: boolean;
  isV2: boolean;
  isCalvo?: boolean;
  stats?: any;
}) {
  // Layout Calvo: sezioni raggruppate (§5 SPECIFICHE-DESIGN, come il mockup)
  // ma con lo styling MenuItem di default (pillola indaco gestita dal CSS
  // [data-theme="calvo"]). Riusa le stesse fasce di V2_MENU_GROUPS.
  if (isCalvo) {
    const sections = buildMenuSections([...items, ...systemItems]);
    return (
      <div className="flex-1 overflow-y-auto p-3">
        {sections.map((section, gi) => (
          <div key={section.label} className={gi > 0 ? 'mt-4' : ''}>
            {isOpen && (
              <div className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--calvo-faint,#9ca3af)]">
                {section.label}
              </div>
            )}
            <nav className="space-y-0.5">
              {section.items.map((item) => (
                <div key={item.path}>
                  <MenuItem item={item} isOpen={isOpen} isV2={false} />
                  {item.path === '/deals' && (
                    <button
                      type="button"
                      data-testid="nav-quick-note"
                      onClick={() => openQuickNoteComposer()}
                      className={cn(
                        'flex w-full items-center rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground',
                        !isOpen && 'justify-center px-2',
                      )}
                      title="Note rapide"
                      aria-label="Note rapide"
                    >
                      <NotebookPen className="h-5 w-5" />
                      {isOpen && <span className="ml-2">Note rapide</span>}
                    </button>
                  )}
                </div>
              ))}
            </nav>
          </div>
        ))}
      </div>
    );
  }

  if (!isV2) {
    // Layout originale flat
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <nav className="space-y-1 mb-6">
          {items.map((item) => (
            <div key={item.path}>
              <MenuItem item={item} isOpen={isOpen} isV2={false} />
              {item.path === '/deals' && (
                <button
                  type="button"
                  data-testid="nav-quick-note"
                  onClick={() => openQuickNoteComposer()}
                  className={cn(
                    'flex w-full items-center rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground',
                    !isOpen && 'justify-center px-2',
                  )}
                  title="Note rapide"
                  aria-label="Note rapide"
                >
                  <NotebookPen className="h-5 w-5" />
                  {isOpen && <span className="ml-2">Note rapide</span>}
                </button>
              )}
            </div>
          ))}
        </nav>
        {isOpen && (
          <div className="mb-4">
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Strumenti Avanzati
            </div>
            <nav className="space-y-1">
              {systemItems.map((item) => (
                <MenuItem key={item.path} item={item} isOpen={isOpen} isV2={false} />
              ))}
            </nav>
          </div>
        )}
      </div>
    );
  }

  // Layout V2 con sezioni
  const sections = buildMenuSections([...items, ...systemItems]);

  return (
    <div className="flex-1 overflow-y-auto py-2">
      {sections.map((section, gi) => (
        <div key={section.label}>
          {gi > 0 && <div className="v2-nav-divider" />}
          {isOpen && (
            <div className="v2-nav-section-label">{section.label}</div>
          )}
          <nav>
            {section.items.map((item) => (
              <div key={item.path}>
                <MenuItem item={item} isOpen={isOpen} isV2={true} />
                {item.path === '/deals' && (
                  <button
                    type="button"
                    data-testid="nav-quick-note"
                    onClick={() => openQuickNoteComposer()}
                    className={cn(
                      'v2-nav-item flex w-full items-center text-sm text-muted-foreground',
                      !isOpen && 'justify-center',
                    )}
                    title="Note rapide"
                    aria-label="Note rapide"
                  >
                    <NotebookPen className="h-5 w-5" />
                    {isOpen && <span className="ml-2">Note rapide</span>}
                  </button>
                )}
              </div>
            ))}
          </nav>
        </div>
      ))}
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactModalInitialData, setContactModalInitialData] = useState<any>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadModalInitialData, setLeadModalInitialData] = useState<any>(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [recordDrawer, setRecordDrawer] = useState<{ type: RecordDrawerEntity; id: number } | null>(null);
  const { theme, setTheme } = useTheme();
  const isV2 = useIsV2();
  const isFjord = isFjordUi();
  const isCalvo = useOperationalIndicators();
  const queryClient = useQueryClient();
  const [currentPath, setLocation] = useLocation();
  const [mobileBackTarget, setMobileBackTarget] = useState(() => crmBackTarget(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    window.history.state, document.referrer, window.location.origin,
  ));
  useEffect(() => installCrmBackTracking(() => setMobileBackTarget(crmBackTarget(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    window.history.state, document.referrer, window.location.origin,
  ))), []);
  const handleMobileBack = () => {
    if (!mobileBackTarget) return;
    if (mobileBackTarget.useBrowserHistory) {
      window.history.back();
    } else {
      window.history.replaceState({ ...window.history.state, __experviserBackPath: null }, '', window.location.href);
      setLocation(mobileBackTarget.path, { replace: true });
    }
  };
  const mobileBackButton = mobileBackTarget && <button
    type="button"
    className="app-shell-header__back lg:hidden"
    onClick={handleMobileBack}
    aria-label="Torna alla pagina precedente"
    title="Torna alla pagina precedente"
  ><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>;
  const mainScrollRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [currentPath]);
  const { openMartina } = useMartinaGlobal();

  // One authenticated, owner-scoped subscription serves the whole CRM.
  useOwnerRealtimeInvalidation();

  // A contact save may include the relation written immediately afterwards.
  // Group the committed writes before active views reread their source.
  useEffect(() => {
    let refreshTimer: number | undefined;
    const pendingResources = new Map<CrmRealtimeResource, number>();
    const refreshActiveQueries = (event: Event) => {
      const detail = (event as CustomEvent<{ resource?: string; committedAt?: number }>).detail;
      const resource = detail?.resource;
      if (!isCrmRealtimeResource(resource)) return;
      pendingResources.set(resource, Math.max(
        pendingResources.get(resource) ?? 0,
        Number(detail?.committedAt) || 0,
      ));
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        const batch = [...pendingResources.entries()];
        pendingResources.clear();
        for (const [pendingResource, committedAt] of batch) {
          void invalidateCrmResourceQueries(queryClient, pendingResource, committedAt);
        }
      }, 160);
    };
    window.addEventListener(CRM_MUTATION_COMMITTED_EVENT, refreshActiveQueries);
    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      window.removeEventListener(CRM_MUTATION_COMMITTED_EVENT, refreshActiveQueries);
    };
  }, [queryClient]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
  }, [queryClient]);

  useEffect(() => {
    const handleOpenContactModal = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      setContactModalInitialData(detail?.initialData ?? null);
      setShowContactModal(true);
    };

    document.addEventListener('openContactModal', handleOpenContactModal);
    return () => document.removeEventListener('openContactModal', handleOpenContactModal);
  }, []);

  useEffect(() => {
    const handleOpenRecordDrawer = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const id = Number(detail?.id);
      if (detail?.type && Number.isSafeInteger(id) && id > 0) {
        setRecordDrawer({ type: detail.type as RecordDrawerEntity, id });
      }
    };
    document.addEventListener('openRecordDrawer', handleOpenRecordDrawer);
    return () => document.removeEventListener('openRecordDrawer', handleOpenRecordDrawer);
  }, []);

  useEffect(() => {
    const handleOpenLeadModal = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      setLeadModalInitialData(detail?.initialData ?? null);
      setShowLeadModal(true);
    };

    document.addEventListener('openLeadModal', handleOpenLeadModal);
    return () => document.removeEventListener('openLeadModal', handleOpenLeadModal);
  }, []);

  useEffect(() => {
    const handleOpenEmailComposer = () => setEmailComposerOpen(true);
    document.addEventListener('openGlobalEmailComposer', handleOpenEmailComposer);
    return () => document.removeEventListener('openGlobalEmailComposer', handleOpenEmailComposer);
  }, []);

  const { data: user } = useQuery<{ success?: boolean; user?: CurrentUser } | CurrentUser, Error, CurrentUser | null>({
    queryKey: ['/api/auth/me'],
    staleTime: 0,
    // Un'indisponibilita' condivisa dell'auth/DB non deve generare tre
    // richieste concorrenti da ogni layout montato.
    retry: shouldRetryCrmQuery,
    select: (payload) => {
      // /api/auth/me returns { success, user }; retain compatibility with
      // older/dev payloads that returned the user object directly.
      if (payload && 'user' in payload) return payload.user ?? null;
      return payload && 'id' in payload ? payload : null;
    },
  });

  const { data: stats } = useQuery<{ emailsUnread?: number }>({ queryKey: ['/api/dashboard/stats'] });
  const { data: briefingPreferences } = useQuery<MartinaBriefingPreferences>({
    queryKey: ['/api/exai/operational-briefing/preferences'],
    staleTime: 60_000,
  });
  const saveNavigationPreferences = useMutation({
    mutationFn: async (pinnedModules: string[]) => apiRequest('/api/exai/operational-briefing/preferences', {
      method: 'PATCH',
      data: { homeSections: { pinnedModules } },
      notifySuccess: false,
    }),
    onSuccess: (next) => {
      queryClient.setQueryData(['/api/exai/operational-briefing/preferences'], next);
    },
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error('Errore logout:', e);
    } finally {
      removeCurrentCrmQueryCache();
      window.localStorage.removeItem('auth_token');
      queryClient.clear();
      window.location.href = '/login';
    }
  };

  const canonicalUserIdentity = user?.fullName?.trim() || user?.username;
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || canonicalUserIdentity?.trim()
    || user?.email?.split('@')[0]?.trim()
    || 'Utente';
  const userInitials = userName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'MA';
  const unreadEmailCount = Number(stats?.emailsUnread ?? 0);

  const mainItems = getMainMenuItems(stats);
  return (
    <>
      <div className="app-root-layout notion-layout flex h-dvh w-full overflow-hidden">
        {/* Mobile Bottom Navigation */}
        {!isFjord && <MobileBottomNav onMenuOpen={() => setIsMobileMenuOpen(true)} />}

        {/* Mobile overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        {!isFjord && <aside className={cn(
          'bg-card border-r border-border fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 shadow-sm',
          isSidebarOpen ? 'w-64' : 'w-16',
          'md:relative md:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}>
          {/* Logo + toggle */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 flex items-center justify-center text-primary-foreground font-bold bg-primary text-sm flex-shrink-0">
                {isSidebarOpen ? 'EX' : 'E'}
              </div>
              {isSidebarOpen && (
                <span className={cn("font-bold text-sm tracking-wide", isV2 ? "text-white" : "text-foreground")}>
                  EXPERVISER CRM
                </span>
              )}
            </div>
            <button
              className="hidden md:flex h-8 w-8 items-center justify-center hover:bg-accent transition-colors flex-shrink-0"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen
                ? <X className="h-4 w-4" />
                : <Menu className="h-4 w-4" />
              }
            </button>
            <button
              className="md:hidden h-8 w-8 flex items-center justify-center"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <OperationalSidebarClock timezone={user?.timezone} isOpen={isSidebarOpen} isV2={isV2} />
          <ApiConnectionIndicator isOpen={isSidebarOpen} isV2={isV2} />
          <ApiBudgetIndicator isOpen={isSidebarOpen} isV2={isV2} />
          <MapsApiStatusIndicator isOpen={isSidebarOpen} isV2={isV2} />

          {/* Navigation */}
          <SidebarNav
            items={mainItems}
            systemItems={systemMenuItems}
            isOpen={isSidebarOpen}
            isV2={isV2}
            isCalvo={isCalvo}
            stats={stats}
          />

          {/* Footer */}
          <div className="mt-auto p-3 border-t border-border flex-shrink-0">
            <button
              onClick={toggleTheme}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-none hover:bg-accent w-full',
                isSidebarOpen ? 'justify-start' : 'justify-center'
              )}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              {isSidebarOpen && <span className="text-sm">Cambia tema</span>}
            </button>
            <button
              onClick={handleLogout}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-none hover:bg-destructive/10 hover:text-destructive w-full mt-1',
                isSidebarOpen ? 'justify-start' : 'justify-center'
              )}
            >
              <LogOut className="h-5 w-5" />
              {isSidebarOpen && <span className="text-sm">Logout</span>}
            </button>
          </div>
        </aside>}

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col h-full overflow-hidden">
          {/* Header / Topbar */}
          <header className="app-shell-header bg-background border-b border-border min-h-16 flex items-center justify-between px-4 z-30 flex-shrink-0">
            {isFjord && <div className={cn('app-shell-header__brand', mobileBackTarget && 'app-shell-header__brand--with-back')}>{mobileBackButton}<Link href="/" className="fjord-wordmark" aria-label="Experviser — Feed"><span aria-hidden="true"><i /><i /><i /></span>experviser</Link></div>}
            {!isFjord && mobileBackButton}
            {/* Mobile menu button */}
            <button
              className={cn('app-shell-header__menu md:hidden h-10 w-10 inline-flex items-center justify-center hover:bg-accent transition-colors', mobileBackTarget && 'hidden')}
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Apri menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Logo mobile */}
            <a
              href="/"
              className="app-shell-header__logo md:hidden text-[#4b53de] text-base font-bold"
              aria-label="Torna al Feed"
            >
              EXPERVISER CRM
            </a>

            <div className="app-shell-header__search mx-3 flex min-w-0 flex-1 justify-center md:mx-6">
              <GlobalCrmSearch isV2={isV2} />
            </div>

            {/* Actions */}
            <div className="app-shell-header__actions flex items-center gap-2">
              <button
                type="button"
                data-testid="fjord-quick-note"
                onClick={() => openQuickNoteComposer()}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-700 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
                title="Aggiungi una nota da qualsiasi pagina"
                aria-label="Note rapide: scegli un record e aggiungi una nota"
              >
                <NotebookPen className="h-4 w-4" aria-hidden="true" />
                <span className="hidden lg:inline">Nota</span>
              </button>
              <details className={isFjord ? 'fjord-operations-menu' : 'contents'}>
                {isFjord && <summary aria-label="Apri strumenti operativi"><SlidersHorizontal /><span>Strumenti</span></summary>}
                <div className={isFjord ? 'fjord-operations-popover' : 'contents'}>
                  <GooglePeopleSyncButton />
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="inline-flex h-8 items-center gap-1.5 border border-border px-2.5 text-xs hover:bg-accent"
                    aria-label="Aggiorna CRM"
                    title="Aggiorna i dati del CRM"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> <span>Aggiorna</span>
                  </button>
                  <CalvoUrgencyCluster />
                  {/* Quick create */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "h-8 inline-flex items-center justify-center hover:bg-accent transition-colors border border-border bg-background gap-1.5 px-3",
                          isV2 && "rounded-md text-sm font-semibold"
                        )}
                        aria-label="Azioni rapide di creazione"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Nuovo</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-slate-50 border-slate-200">
                      <DropdownMenuItem asChild><a href="/contacts?new=true"><Users className="h-4 w-4 mr-2" />Nuovo Contatto</a></DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/companies?new=true"><Building2 className="h-4 w-4 mr-2" />Nuova Azienda</a></DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/deals?new=true"><Briefcase className="h-4 w-4 mr-2" />Nuova Opportunità</a></DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/leads?new=true"><Target className="h-4 w-4 mr-2" />Nuovo Lead</a></DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setTimeout(() => document.dispatchEvent(new CustomEvent('openGlobalEmailComposer')), 0)}><MailPlus className="h-4 w-4 mr-2" />Nuova Email</DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/calendar?new=true"><Calendar className="h-4 w-4 mr-2" />Nuovo Appuntamento</a></DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/calendar?new=event"><CalendarRange className="h-4 w-4 mr-2" />Nuovo Evento</a></DropdownMenuItem>
                      <DropdownMenuItem asChild><a href="/tasks?new=true"><CheckSquare className="h-4 w-4 mr-2" />Nuova Attività</a></DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DebugButton />
                  <CalendarInviteIssueIndicator />
                </div>
              </details>
              {(isFjord || unreadEmailCount > 0) && (
                <a
                  href="/emails?queue=inbox"
                  className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 tabular-nums hover:bg-emerald-100"
                  title={`${unreadEmailCount} ${unreadEmailCount === 1 ? 'email nuova' : 'email nuove'} — apri Posta in arrivo`}
                  aria-label={`${unreadEmailCount} ${unreadEmailCount === 1 ? 'email nuova' : 'email nuove'}`}
                >
                  <Mail className="h-3.5 w-3.5" />
                  {isFjord && <span>Email</span>}{unreadEmailCount > 0 ? unreadEmailCount : null}
                </a>
              )}
              <NotificationIndicator />
              {isFjord && <button type="button" className="fjord-martina-button" aria-label="Apri Martina" onClick={() => openMartina({ context: inferContextFromLocation() })}><Sparkles /><span>Martina</span></button>}
            </div>

            {/* Profile */}
            <div className="app-shell-header__profile">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors rounded-none"
                    aria-label="Menu profilo"
                  >
                    <div className="w-8 h-8 rounded-none bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                      {userInitials}
                    </div>
                    <ChevronDown className="h-4 w-4 hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-0 bg-slate-50 border-slate-200" sideOffset={8}>
                  <div className="px-3 py-3 border-b border-border">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-none bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                        {userInitials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{userName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email ?? ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    {isFjord && <DropdownMenuItem asChild><a href={uiRecoveryHref()}>Ripristina UI precedente</a></DropdownMenuItem>}
                    <DropdownMenuItem
                      onSelect={() => setLocation('/settings/account')}
                      className="flex items-center px-3 py-2 text-sm cursor-pointer"
                    >
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />Il mio profilo
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setLocation('/settings')}
                      className="flex items-center px-3 py-2 text-sm cursor-pointer"
                    >
                      <Settings className="h-4 w-4 mr-2 text-muted-foreground" />Impostazioni sistema
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator />
                  <div className="py-1">
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center px-3 py-2 text-sm cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2 text-muted-foreground" />Logout
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          {isFjord && <FjordNavigation
            items={[...mainItems, ...systemMenuItems]}
            preferredPaths={briefingPreferences?.homeSections?.pinnedModules}
            onPreferredPathsChange={(paths) => saveNavigationPreferences.mutate(paths)}
            onLogout={handleLogout}
            status={<>
            <OperationalSidebarClock timezone={user?.timezone} isOpen={true} isV2={false} />
            <ApiConnectionIndicator isOpen={true} isV2={false} />
            <ApiBudgetIndicator isOpen={true} isV2={false} />
            <MapsApiStatusIndicator isOpen={true} isV2={false} />
          </>}
          />}

          <MartinaUsageSuspendedBanner />
          <main ref={mainScrollRef} className={cn(
            'app-main-scroll min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-clip overscroll-x-none overscroll-y-contain bg-background [&>*]:max-w-full [&>*]:min-w-0 [&>*]:overflow-x-clip',
            // Conserva spazio per la nav mobile e lascia sempre raggiungibili
            // gli ultimi comandi anche nelle finestre desktop/tablet basse.
            'pb-32 md:pb-16'
          )}>{children}</main>
        </div>

        <DebugConsole />
        <MartinaPushBanner />
        <ContactModal
          open={showContactModal}
          onOpenChange={(open) => {
            setShowContactModal(open);
            if (!open) setContactModalInitialData(null);
          }}
          initialData={contactModalInitialData}
        />
        <LeadModal
          open={showLeadModal}
          onOpenChange={(open) => {
            setShowLeadModal(open);
            if (!open) setLeadModalInitialData(null);
          }}
          initialData={leadModalInitialData}
        />
        <EmailModal
          open={emailComposerOpen}
          onOpenChange={setEmailComposerOpen}
        />
        <QuickNoteDialog />
        <RecordDrawer
          open={recordDrawer !== null}
          onClose={() => setRecordDrawer(null)}
          title="Origine del segnale"
        >
          {recordDrawer?.type === 'contact' && <ContactDrawerContent id={recordDrawer.id} onOpenFullProfile={(id) => { setRecordDrawer(null); setLocation(`/contacts/${id}`); }} />}
          {recordDrawer?.type === 'company' && <CompanyDrawerContent id={recordDrawer.id} />}
          {recordDrawer?.type === 'branch' && <BranchDrawerContent id={recordDrawer.id} />}
          {recordDrawer?.type === 'lead' && <LeadDrawerContent id={recordDrawer.id} />}
          {recordDrawer?.type === 'deal' && <DealDrawerContent id={recordDrawer.id} />}
        </RecordDrawer>
      </div>
    </>
  );
}
